import React, {useEffect, useState} from 'react';
import { Helmet } from 'react-helmet-async';
import { Outlet, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { t } from 'ttag';
import vocab from '../../../config/channels';
import { TChannel } from '../../../contexts/operator/operator-context';
import useOperatorContext from '../../../contexts/operator/useOperatorContext';
import { TSite } from '../../../contexts/site/site-context';
import { TDeliveryVenue } from '../../../contexts/delivery-venue/delivery-venue-context';
import useSiteContext from '../../../contexts/site/useSiteContext';
import useDeliveryVenueContext from '../../../contexts/delivery-venue/useDeliveryVenueContext';
import PageSpinnerThree from '../../general/PageSpinnerThree';
import AppLayout from '../../layouts/app/AppLayout';
import ChannelSelector from './components/ChannelSelector';
import DeliveryTab from './components/DeliveryTab';
import EatInTab from "./components/EatInTab";
import SiteFilter from "./components/SiteFilter";
import DeliveryVenueFilter from "./components/DeliveryVenueFilter";
import useChannelSelectSetup from "./useChannelSelectSetup";
import {iLog} from "../../../index";
import { useSiteSelectView } from '../../../services/siteSelectTracking';
import { singleSiteRedirect } from './singleSiteRedirect';
import powered_by from "../../../assets/images/OrderswiftLogo2025.png";

type ChannelSelectPageProps = {
    channel?: TChannel | null;
};

const ChannelSelect = ({ channel }: ChannelSelectPageProps) => {
    const operatorContext = useOperatorContext();
    const siteContext = useSiteContext();
    const deliveryVenueContext = useDeliveryVenueContext();
    const navigate = useNavigate();

    const [enabledChannels, setEnabledChannels] = useState<TChannel[]>([]);
    const [activeChannelTab, setActiveChannelTab] = useState('');

    useChannelSelectSetup();

    useEffect(() => {
        if ( siteContext.sites?.length === 1 ) {
            let singleSite = siteContext.sites[0];
            let enabledChannels = Object.keys(singleSite.channels);

            if ( enabledChannels.length === 1 ) {
                if (enabledChannels[0] === 'dine_in') {
                    navigate(`/${vocab.dineIn.handle}/${singleSite.handle}/table`)
                } else if ( ['delivery', 'catering', 'venues'].includes(enabledChannels[0]) ) {
                    // nothing because we want the postcode input or venue selection
                } else {
                    navigate(`/${enabledChannels[0]}/${singleSite.handle}`)
                }
            }
        }
    }, [siteContext.sites]);

    useEffect(() => {
        if (operatorContext.operator?.channels.collection && channel !== 'dine_in') {
            setEnabledChannels(enabledChannels => [...enabledChannels, vocab.collection.name]);
        }

        if (operatorContext.operator?.channels.delivery && channel !== 'dine_in') {
            setEnabledChannels(enabledChannels => [...enabledChannels, vocab.delivery.name]);
        }

        if (operatorContext.operator?.channels.catering && channel !== 'dine_in') {
            setEnabledChannels(enabledChannels => [...enabledChannels, vocab.catering.name ]);
        }

        if (operatorContext.operator?.channels.venues && channel !== 'dine_in') {
            setEnabledChannels(enabledChannels => [...enabledChannels, vocab.venues.name ]);
        }

        if (channel === 'dine_in') {
            setEnabledChannels(enabledChannels => [...enabledChannels, vocab.dineIn.name]);
        }
    }, []);

    useEffect(() => {
        setActiveChannelTab(channel ?? enabledChannels[0]);
    }, [channel, enabledChannels])

    useSiteSelectView(
        'page',
        !!siteContext.sites && !!deliveryVenueContext.deliveryVenues && !singleSiteRedirect(siteContext.sites) && !!activeChannelTab,
        { sites_count: siteContext.sites?.length, channel_tabs: enabledChannels.length, start_tab: activeChannelTab }
    );

    if (!siteContext || !siteContext.sites || !deliveryVenueContext || !deliveryVenueContext.deliveryVenues) {
        return <PageSpinnerThree show={true} />;
    }

    const collectionSites = siteContext.sites?.filter((site) => site.channels.collection);
    const deliveryVenues = deliveryVenueContext.deliveryVenues;
    const dineInSites = siteContext.sites?.filter((site) => site.channels.dine_in);

    const renderChannel = () => {
        switch (activeChannelTab) {
            case vocab.collection.name:
                return (
                    <>
                        <SiteFilter key="collection_filter" sites={collectionSites as TSite[]} channel={vocab.collection} />
                    </>
                );

            case vocab.delivery.name:
                return (
                    <>
                        <DeliveryTab channel={'delivery'} />
                    </>
                );

            case vocab.catering.name:
                return (
                    <>
                        <DeliveryTab channel={'catering'} />
                    </>
                );

            case vocab.venues.name:
                return (
                    <>
                        <DeliveryVenueFilter key="delivery_venue_filter" deliveryVenues={deliveryVenues as TDeliveryVenue[]} />
                    </>
                );

            case vocab.dineIn.name:
                return (
                    <>
                        <SiteFilter key="dine_in_filter" sites={dineInSites as TSite[]} channel={vocab.dine_in} />
                    </>
                );

            default:
                return <p>{t`No channel selected.`}</p>;
        }
    };

    return (
        <AppLayout page="channels">
            <Helmet>
                <title>
                    {operatorContext.operator?.name ?? ''} {t`Online Ordering - Powered by Orderswift`}
                </title>
                <link rel="icon" href={operatorContext.operator?.theme?.v4.favicon ?? ''} />
            </Helmet>

            <ChannelSelector
                selectedChannel={activeChannelTab}
                enabledChannels={enabledChannels}
                setActiveChannelTab={setActiveChannelTab}
            >
                <ChannelContent>
                    <span className='d-md-none mobile_bg' />
                    <div className='channelWrapper'>{renderChannel()}</div>
                    <div className='orderswift-banner'>
                        <img width={169} height={15} src={powered_by} alt={'Powered by Orderswift'} />
                    </div>
                </ChannelContent>
            </ChannelSelector>

            <Outlet />
        </AppLayout>
    );
};

const ChannelContent = styled.div`
    flex-grow: 1;
    margin-top: 24px;

    .orderswift-banner {
        z-index: 999;
        display: flex;
        justify-content: center;
        position: fixed;
        bottom: 0;
        left: 0;
        right: 0;
        background: #FFFFFF;
        border-top: 2px solid #000000;
        padding: 10px 0;

        @media screen and (min-width: 720px) {
            width: 50%;
            right: auto;
        }
    }
`;

export default ChannelSelect;
